import invariant from "invariant";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCurrentUser from "../../../hooks/useCurrentUser";
import { getErrorContent, ImportMeetingError } from "../importMeetingErrors";
import { IntegrationMessage, VideoIntegration } from "../IntegrationMessage";
import { ImportBotMeeting } from "./ImportBotMeeting";
import { ImportZoomNativeMeeting } from "./ImportZoomNativeMeeting";
import { StartAsInPerson } from "./StartAsInPerson";
import { checkIsPmi, parseMeetingDetails } from "./utils";

type ImportVideoMeetingProps = {
  integrationName: string;
  isInPerson: boolean;
  inZoomApp: boolean;
  inTeamsApp: boolean;
};

export const ImportVideoMeeting: React.FC<ImportVideoMeetingProps> = ({
  integrationName,
  isInPerson,
  inZoomApp,
  inTeamsApp,
}) => {
  const currentUser = useCurrentUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { meetingUrl, botMeetingId, zoomMeetingId, entryPoint, forceImport } =
    parseMeetingDetails(integrationName, searchParams);

  const videoIntegration =
    integrationName === "zoom"
      ? VideoIntegration.ZOOM
      : integrationName === "googleMeet"
      ? VideoIntegration.GOOGLE_MEET
      : integrationName === "teams"
      ? VideoIntegration.TEAMS
      : null;
  invariant(videoIntegration, "Missing video integration");

  if (isInPerson)
    return (
      <StartAsInPerson
        meetingUrl={meetingUrl}
        botMeetingId={botMeetingId}
        zoomMeetingId={zoomMeetingId}
      />
    );

  const isPmi =
    videoIntegration === VideoIntegration.ZOOM && checkIsPmi(zoomMeetingId);

  if (isPmi) {
    const errorMessage = getErrorContent(ImportMeetingError.PMI);
    return (
      <IntegrationMessage
        isError
        primaryMessage={errorMessage.primary}
        secondaryMessage={errorMessage.secondary}
        videoIntegration={videoIntegration}
        videoMeetingUrl={meetingUrl}
        showLaunchButton={!inZoomApp && !inTeamsApp}
        inZoomApp={inZoomApp}
        inTeamsApp={inTeamsApp}
        showImportButton={inTeamsApp}
      />
    );
  }

  const isZoomNative =
    videoIntegration === VideoIntegration.ZOOM &&
    currentUser.organization.isZoomAuthorized;

  const checkZoomJoinRedirect = (errorMessage: string): void => {
    if (!inZoomApp) return;
    /**
     * TODO: it would be better to send an error code from the backend
     * and look at that here instead of the error message.
     */
    if (["Meeting not found", "Invalid interviewer"].includes(errorMessage)) {
      let url = "/join";
      if (meetingUrl) {
        url += `?location=${meetingUrl}`;
      }
      navigate(url);
    }
  };

  if (isZoomNative) {
    return (
      <ImportZoomNativeMeeting
        inZoomApp={inZoomApp}
        zoomMeetingId={zoomMeetingId}
        meetingUrl={meetingUrl}
        entryPoint={entryPoint}
        forceImport={forceImport}
        onError={checkZoomJoinRedirect}
      />
    );
  }

  return (
    <ImportBotMeeting
      inZoomApp={inZoomApp}
      inTeamsApp={inTeamsApp}
      botMeetingId={botMeetingId}
      meetingUrl={meetingUrl}
      entryPoint={entryPoint}
      forceImport={forceImport}
      videoIntegration={videoIntegration}
      onError={checkZoomJoinRedirect}
    />
  );
};
